import * as React from "react";

import { styled, useTheme } from "@mui/material/styles";

import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";

import Toolbar from "@mui/material/Toolbar";

import Typography from "@mui/material/Typography";

import IconButton from "@mui/material/IconButton";

import Menu from "@mui/material/Menu";

import MenuIcon from "@mui/icons-material/Menu";

import MenuItem from "@mui/material/MenuItem";

import Divider from "@mui/material/Divider";

import ListItemText from "@mui/material/ListItemText";

import Avatar from "@mui/material/Avatar";

import Box from "@mui/material/Box";

import { useAppDispatch, useAppSelector } from "../hooks/store";

import { clearSession } from "../features/auth/authSlice";

import { currentTitle } from "../app/uiSlice";

import { useCurrentUser } from "../hooks/useCurrentUser";


import { useNavigate } from "react-router-dom";

import { CardBase } from "./global";

const drawerWidth: number = 240 - 1;

interface AppBarProps extends MuiAppBarProps {

  open?: boolean;

}

const StyledNavbarContainer = styled(Box, {

  shouldForwardProp: (prop) => prop !== "open",

})<AppBarProps>(({ theme, open }) => ({

  position: "absolute",

  top: 0,

  left: 0,

  right: 0,

  zIndex: theme.zIndex.drawer + 2,

  transition: theme.transitions.create(["width", "margin"], {

    easing: theme.transitions.easing.sharp,

    duration: theme.transitions.duration.leavingScreen,

  }),

  [theme.breakpoints.up("sm")]: {

    ...(open && {

      marginLeft: drawerWidth,

      width: `calc(100% - ${drawerWidth}px)`,

      transition: theme.transitions.create(["width", "margin"], {

        easing: theme.transitions.easing.sharp,

        duration: theme.transitions.duration.enteringScreen,

      }),

    }),

  },

}));

const Navbar = ({

  open,

  onToggle,

}: {

  open: boolean;

  onToggle: () => void;

}) => {

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const user = useCurrentUser();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(

    null

  );

  const pageTitle = useAppSelector(currentTitle);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {

    setAnchorElUser(event.currentTarget);

  };

  const handleCloseUserMenu = () => {

    setAnchorElUser(null);

  };

  const handleAccountMenu = () => {

    navigate("account");

    setAnchorElUser(null);

  };

  const handleLogout = () => {

    dispatch(clearSession());

    setAnchorElUser(null);

  };

  return (

    <StyledNavbarContainer

      open={open}

      sx={{

        zIndex: { xs: (theme) => theme.zIndex.drawer + 2, sm: (theme) => theme.zIndex.drawer + 1 }

      }}

    >

      <CardBase

        useGlassCard={true}

        nested={false}

        glassEffect="none"

        sx={{

          borderRadius: "0 !important",

          border: "none !important",

          boxShadow: (theme) =>

            theme.palette.mode === "dark"

              ? "0 2px 8px rgba(0, 0, 0, 0.3)"

              : "0 2px 8px rgba(0, 0, 0, 0.1)",

          p: "0 !important",

          m: "0 !important",

          minHeight: { xs: "30px", sm: "44px" },

          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

          position: "relative",

          // Orange to white gradient background with more opacity for darker color

          background: {

            xs: "linear-gradient(180deg, rgba(244, 162, 89, 0.95) 0%, rgba(245, 175, 110, 0.85) 45%, rgba(255, 255, 255, 0.7) 100%) !important",

            sm: "linear-gradient(180deg, rgba(244, 162, 89, 0.95) 0%, rgba(245, 170, 105, 0.85) 40%, rgba(255, 255, 255, 0.65) 100%) !important"

          },

          backdropFilter: "blur(12px) saturate(180%)",

          WebkitBackdropFilter: "blur(12px) saturate(180%)",

          // Override any default CardBase/GlassCard styles

          "&::before": {

            borderRadius: "0 !important",

            boxShadow: "none !important",

            display: "none",

          },

          "& > div": {

            background: "transparent !important",

          },

          // Ensure Card component from GlassCard doesn't override background

          "&.MuiCard-root": {

            background: {

              xs: "linear-gradient(180deg, rgba(244, 162, 89, 0.95) 0%, rgba(245, 175, 110, 0.85) 45%, rgba(255, 255, 255, 0.7) 100%) !important",

              sm: "linear-gradient(180deg, rgba(244, 162, 89, 0.95) 0%, rgba(245, 170, 105, 0.85) 40%, rgba(255, 255, 255, 0.65) 100%) !important"

            },

            borderRadius: "0 !important",

          },

        }}

      >

        <Toolbar

          sx={{

            pr: { xs: 0.5, sm: 1, md: 1.5 },

            pl: { xs: 0.5, sm: 1 },

            py: 0,

            minHeight: { xs: "36px !important", sm: "44px !important" },

            position: "relative",

            zIndex: 2,

            display: "flex",

            justifyContent: "space-between",

            alignItems: "center",

            gap: { xs: 0.5, sm: 1 },

            width: "100%",

          }}

        >

          <Box

            sx={{

              display: "flex",

              alignItems: "center",

              gap: { xs: 0.5, sm: 1, md: 1.5 },

              flex: { xs: "0 0 auto", sm: "0 0 auto" },

              minWidth: 0,

            }}

          >

            <IconButton

              edge="start"

              color="inherit"

              aria-label="open drawer"

              onClick={(e) => {

                e.preventDefault();

                e.stopPropagation();

                onToggle();

              }}

              sx={{

                padding: { xs: 0.25, sm: 0.5 },

                margin: 0,

                zIndex: 1300, // Ensure it's above drawer

                position: "relative",

                cursor: "pointer",

              }}

            >

              <MenuIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, color: "#ffffff" }} />

            </IconButton>

            <Typography

              component="h1"

              variant="h6"

              noWrap

              sx={{

                color: "#ffffff",

                fontWeight: 600,

                fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",

                maxWidth: { xs: "120px", sm: "200px", md: "300px", lg: "none" },

                minWidth: 0,

              }}

            >

              {pageTitle}

            </Typography>

          </Box>

          <Box

            sx={{

              display: "flex",

              alignItems: "center",

              gap: { xs: 0.25, sm: 0.5 },

              flex: { xs: "0 0 auto", sm: "0 0 auto" },

              minWidth: 0,

            }}

          >

            <Box

              component="img"

              src="/logo192.png"

              alt="Workz360 logo"

              sx={{

                width: { xs: 32, sm: 38 },

                height: { xs: 32, sm: 38 },

                borderRadius: "50%",

                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",

                backgroundColor: "rgba(255,255,255,0.6)",

              }}

            />

            <IconButton

              color="inherit"

              sx={{

                padding: { xs: 0.25, sm: 0.5 },

                margin: 0,

              }}

              onClick={handleOpenUserMenu}

            >

              <Avatar

                alt={user?.first_name || ""}

                src={

                  !!user?.photo?.length

                    ? user?.photo[0].original_url

                    : "/no-picture"

                }

                sx={{

                  width: { xs: 32, sm: 36, md: 40 },

                  height: { xs: 32, sm: 36, md: 40 },

                  boxShadow:

                    "0 4px 12px rgba(0,0,0,0.25), 0 0 0 2px rgba(255,255,255,0.4)",

                  backgroundColor: "rgba(255,255,255,0.2)",

                  backdropFilter: "blur(6px)",

                }}

              />

            </IconButton>

          </Box>

          {/* User Menu */}

          <Menu

            sx={{

              mt: { xs: "36px", sm: "45px" },

              "& .MuiPaper-root": {

                minWidth: { xs: 200, sm: 240 },

              },

            }}

            id="menu-appbar"

            anchorEl={anchorElUser}

            anchorOrigin={{

              vertical: "top",

              horizontal: "right",

            }}

            keepMounted

            transformOrigin={{

              vertical: "top",

              horizontal: "right",

            }}

            open={Boolean(anchorElUser)}

            onClose={handleCloseUserMenu}

          >

            <MenuItem key={"account"} onClick={handleAccountMenu}>

              <ListItemText

                primaryTypographyProps={{

                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },

                }}

              >

                Account

              </ListItemText>

            </MenuItem>

            <Divider />

            <MenuItem key={"logout"} onClick={handleLogout}>

              <ListItemText

                primaryTypographyProps={{

                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },

                }}

              >

                Logout

              </ListItemText>

            </MenuItem>

          </Menu>

        </Toolbar>

      </CardBase>

    </StyledNavbarContainer>

  );

};

export default Navbar;
